import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { QuickAddComponent } from '@theme/quick-add';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * ONÉRO — gaveta do carrinho.
 * `onero-quick-add`: Add do "You may also like" abre a janela de produto do tema (quick add),
 *   com visual próprio quando vem do carrinho (`dialog[data-onero-cart]`,
 *   snippets/onero-quick-view-styles.liquid).
 * `onero-cart-add`: Add da peça grátis (entra direto, com `_onero_gift`).
 * `onero-cart-sync`: tira a peça grátis quando o carrinho cai abaixo da barra 3.
 * As setas do "You may also like" rolam o carrossel.
 * Depois de cada mudança, a gaveta se redesenha pelo fluxo normal do tema (sections).
 */

const sectionIds = () =>
  [...document.querySelectorAll('cart-items-component')]
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean);

const getCart = () =>
  fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  }).then((res) => res.json());

class OneroCartAdd extends HTMLElement {
  #abort = new AbortController();
  #busy = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#abort = new AbortController();
  }

  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-add]') : null;
    if (!button || !this.contains(button)) return;
    this.#add(button);
  };

  async #add(button) {
    if (this.#busy) return;
    const select = this.querySelector('[data-variant-select]');
    const id = Number(select?.value);
    if (!id) return;

    this.#busy = true;
    button.setAttribute('aria-busy', 'true');
    this.#hideError();

    const item = { id, quantity: 1 };
    if (this.dataset.gift === 'true') item.properties = { _onero_gift: '1' };

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item], sections: sectionIds().join(',') }),
      }).then((res) => res.json());
      const cart = await getCart();

      if (response.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { source: 'onero-cart-add', items: cart.items, itemCount: cart.item_count, didError: true },
        });
        this.#showError(response.description || response.message || 'Could not add to cart.');
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          source: 'onero-cart-add',
          items: cart.items,
          itemCount: cart.item_count,
          sections: response.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
      );
      this.#showError('Could not add to cart. Try again.');
    } finally {
      this.#busy = false;
      button.removeAttribute('aria-busy');
    }
  }

  #showError(message) {
    const error = this.querySelector('[data-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
  }

  #hideError() {
    const error = this.querySelector('[data-error]');
    if (error) error.hidden = true;
  }
}

class OneroCartSync extends HTMLElement {
  static observedAttributes = ['data-remove-key'];
  #running = false;

  connectedCallback() {
    this.#run();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#run();
  }

  async #run() {
    const key = this.dataset.removeKey;
    if (!key || this.#running) return;
    this.#running = true;

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: [{ id: key, quantity: 0 }],
        promise: deferred.promise,
      })
    );

    try {
      const cart = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0, sections: sectionIds().join(',') }),
      }).then((res) => res.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          source: 'onero-cart-sync',
          items: cart.items,
          itemCount: cart.item_count,
          sections: cart.sections,
          didError: Boolean(cart.status),
        },
      });
    } catch (error) {
      deferred.reject(error);
    } finally {
      this.#running = false;
    }
  }
}

class OneroQuickAdd extends QuickAddComponent {
  #closeTimer = 0;

  get productPageUrl() {
    return this.dataset.productUrl || '';
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-quick-add]') : null;
    if (!button || button.getAttribute('aria-busy') === 'true') return;

    const host = document.getElementById('quick-add-dialog');
    const dialog = host?.querySelector('dialog');
    clearTimeout(this.#closeTimer);
    dialog?.setAttribute('data-onero-cart', '');
    if (host) host.dataset.oneroUrl = this.productPageUrl;
    host?.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        // espera a animação de fechar antes de voltar ao visual padrão
        this.#closeTimer = window.setTimeout(() => dialog?.removeAttribute('data-onero-cart'), 400);
      },
      { once: true }
    );

    button.setAttribute('aria-busy', 'true');
    try {
      await this.handleClick(event);
    } finally {
      button.removeAttribute('aria-busy');
    }
  };
}

/**
 * Lápis ao lado da variante: abre cor/tamanho da linha. "Update" tira a linha e põe a
 * variante nova com a mesma quantidade e as mesmas propriedades (inclusive `_onero_gift`).
 */
class OneroLineEdit extends HTMLElement {
  #abort = new AbortController();
  #busy = false;

  connectedCallback() {
    const data = JSON.parse(this.querySelector('[data-ole-data]')?.textContent || '{}');
    this.variants = data.variants || [];
    this.properties = data.properties || {};
    const { signal } = this.#abort;
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#sync, { signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#abort = new AbortController();
  }

  get #panel() {
    return this.querySelector('[data-ole-panel]');
  }

  get #selects() {
    return [...this.querySelectorAll('[data-ole-option]')];
  }

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('[data-ole-toggle]')) this.#toggle();
    else if (target?.closest('[data-ole-save]')) this.#save();
  };

  #toggle(force) {
    const panel = this.#panel;
    if (!panel) return;
    const open = force ?? panel.hidden;
    panel.hidden = !open;
    this.querySelector('.ole__pencil')?.setAttribute('aria-expanded', String(open));
    if (open) this.#sync();
  }

  #selected() {
    const values = this.#selects.map((select) => select.value);
    return this.variants.find((variant) => variant.options.every((value, index) => value === values[index]));
  }

  // Marca as combinações esgotadas e só libera o Update numa variante nova e disponível.
  #sync = () => {
    const selects = this.#selects;
    selects.forEach((select, index) => {
      for (const option of select.options) {
        const values = selects.map((s, i) => (i === index ? option.value : s.value));
        const match = this.variants.find((variant) => variant.options.every((value, i) => value === values[i]));
        option.disabled = !match?.available;
      }
    });
    const variant = this.#selected();
    const save = this.querySelector('[data-ole-save]');
    if (save) save.disabled = !variant?.available || String(variant.id) === this.dataset.variantId;
  };

  async #save() {
    const variant = this.#selected();
    if (this.#busy || !variant?.available || String(variant.id) === this.dataset.variantId) return;
    this.#busy = true;
    const save = this.querySelector('[data-ole-save]');
    save?.setAttribute('aria-busy', 'true');
    this.#hideError();

    const quantity = Number(this.dataset.quantity) || 1;
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [{ merchandiseId: String(variant.id), quantity }],
        promise: deferred.promise,
      })
    );

    try {
      await fetch(`${Theme.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: this.dataset.key, quantity: 0 }),
      });
      const item = { id: variant.id, quantity };
      if (Object.keys(this.properties).length) item.properties = this.properties;
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item], sections: sectionIds().join(',') }),
      }).then((res) => res.json());
      const cart = await getCart();

      if (response.status) {
        // não deu pra pôr a nova: redesenha a gaveta mesmo assim (a antiga já saiu) e avisa
        const sections = await fetch(`${window.location.pathname}?sections=${sectionIds().join(',')}`).then((res) =>
          res.json()
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { source: 'onero-line-edit', items: cart.items, itemCount: cart.item_count, sections, didError: true },
        });
        this.#showError(response.description || response.message || 'Could not update this item.');
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          source: 'onero-line-edit',
          items: cart.items,
          itemCount: cart.item_count,
          sections: response.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      this.#showError('Could not update this item. Try again.');
    } finally {
      this.#busy = false;
      save?.removeAttribute('aria-busy');
    }
  }

  #showError(message) {
    const error = this.querySelector('[data-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
  }

  #hideError() {
    const error = this.querySelector('[data-error]');
    if (error) error.hidden = true;
  }
}

/**
 * "Size guide" dentro da janela rápida. A cópia do guia que vem com a página do produto fica
 * escondida lá dentro (o tema esconde tudo que não é cor/tamanho/botão), e abrir uma janela
 * escondida travava a tela. Aqui o guia sai pra fora do conteúdo antes de abrir; no celular,
 * onde o tema remonta a janela sem ele, busca na página do produto.
 */
const bindSizeGuideTabs = (guide) => {
  const root = guide.querySelector('dialog');
  if (!root) return;
  root.querySelectorAll('[data-osg-unit]').forEach((tab) => {
    tab.addEventListener('click', () => {
      root.querySelectorAll('[data-osg-unit]').forEach((other) => {
        const on = other === tab;
        other.classList.toggle('is-active', on);
        other.setAttribute('aria-selected', String(on));
      });
      root.querySelectorAll('[data-osg-table]').forEach((wrap) => {
        wrap.hidden = wrap.dataset.osgTable !== tab.dataset.osgUnit;
      });
    });
  });
};

window.oneroOpenSizeGuide = async (button) => {
  const modal = button.closest('.quick-add-modal');
  if (!modal) {
    const scope = button.closest('.shopify-section') || document;
    (scope.querySelector('dialog-component.onero-size-guide') || document.querySelector('dialog-component.onero-size-guide'))?.showDialog();
    return;
  }

  const host = document.getElementById('quick-add-dialog');
  const url =
    host?.dataset.oneroUrl ||
    modal.querySelector('.view-product-title a, .product-header > a')?.getAttribute('href') ||
    '';
  let guide = modal.querySelector(':scope > dialog-component.onero-size-guide');

  if (!guide || guide.dataset.oneroUrl !== url) {
    guide?.remove();
    guide = modal.querySelector('.quick-add-modal__content dialog-component.onero-size-guide');
    if (!guide && url) {
      try {
        const html = await fetch(url).then((res) => res.text());
        const found = new DOMParser().parseFromString(html, 'text/html').querySelector('dialog-component.onero-size-guide');
        if (found) guide = document.importNode(found, true);
      } catch (error) {
        guide = null;
      }
    }
    if (!guide) return;
    guide.dataset.oneroUrl = url;
    guide.classList.add('onero-size-guide--floating');
    modal.append(guide);
    bindSizeGuideTabs(guide);
  }

  await customElements.whenDefined('dialog-component');
  guide.showDialog();
};

if (!customElements.get('onero-cart-add')) customElements.define('onero-cart-add', OneroCartAdd);
if (!customElements.get('onero-line-edit')) customElements.define('onero-line-edit', OneroLineEdit);
if (!customElements.get('onero-quick-add')) customElements.define('onero-quick-add', OneroQuickAdd);
if (!customElements.get('onero-cart-sync')) customElements.define('onero-cart-sync', OneroCartSync);

// Setas do "You may also like" (a gaveta é redesenhada, então escuta no document).
document.addEventListener('click', (event) => {
  const arrow = event.target instanceof Element ? event.target.closest('[data-ocu-dir]') : null;
  if (!(arrow instanceof HTMLElement)) return;
  const track = arrow.closest('.ocu')?.querySelector('[data-ocu-track]');
  if (!(track instanceof HTMLElement)) return;
  const step = track.clientWidth + 12;
  const max = track.scrollWidth - track.clientWidth;
  const dir = Number(arrow.dataset.ocuDir);
  let next = track.scrollLeft + dir * step;
  if (next > max + 1) next = 0;
  if (next < -1) next = max;
  track.scrollTo({ left: next, behavior: 'smooth' });
});
